import { useEffect, useState } from "react";
import { authService } from "../../services/api";
import "./index.css";
import { useNotification } from "../../components/Notifications/NotificationContext";
import TrackingList from "./TrackingList";
import TrackingForm from "./TrackingForm";
import PixelTestModal, { precheckPixelTest } from "./PixelTestModal";
import SelectPlatformForTrackingProfile from "../../components/Modal/SelectPlatformForTrackingProfile";
import { useAuth } from "../../context/AuthProvider.jsx";
import { getVisibleBots } from "../../utils/bots";


export default function Tracking() {
    const { showError, showSuccess } = useNotification();
    const { user } = useAuth();

    const [loading, setLoading] = useState(true);
    const [trackingProfiles, setTrackingProfiles] = useState([]);
    const [editing, setEditing] = useState(null);
    const [showForm, setShowForm] = useState(false);
    const [showPlatformModal, setShowPlatformModal] = useState(false);
    const [selectedPlatform, setSelectedPlatform] = useState(null);

    // Fetch tracking profiles
    useEffect(() => {
        fetchTrackingProfiles();
    }, []);

    async function fetchTrackingProfiles() {
        try {
            setLoading(true);
            const res = await authService.tracking.list_tracking();
            const json = res.data;

            if (!json.success) {
                showError(json.error || "Erro ao carregar perfis de tracking");
                return;
            }

            setTrackingProfiles(json.data.trackingProfiles || []);
        } catch (err) {
            showError("Erro ao carregar perfis de tracking");
            console.error(err);
        } finally {
            setLoading(false);
        }
    }

    const userBots = getVisibleBots(user?.bots);

    const handleEdit = (profile) => {
        setSelectedPlatform(profile.platform || 'facebook');
        setEditing(profile);
        setShowForm(true);
        setShowPlatformModal(false);
    };

    const handleNewProfile = () => {
        setEditing(null);
        setSelectedPlatform(null);
        setShowPlatformModal(true);
    };

    const handleSelectPlatform = (platform) => {
        setSelectedPlatform(platform);
        setShowPlatformModal(false);
        setShowForm(true);
    };



    const handleSave = async (formData) => {
        try {
            await authService.tracking.create_tracking_profile(formData);
            showSuccess(editing ? "Perfil atualizado!" : "Perfil criado!");
            setEditing(null);
            setShowForm(false);
            await fetchTrackingProfiles();
        } catch (err) {
            showError(err.response?.data?.error || "Erro ao salvar perfil");
            throw err;
        }
    };

    const handleDelete = async (profileId) => {
        if (!window.confirm("Tem certeza que deseja deletar este perfil?")) {
            return;
        }

        try {
            await authService.tracking.delete_profile(profileId);
            showSuccess("Perfil deletado com sucesso!");
            await fetchTrackingProfiles();
        } catch (err) {
            showError("Erro ao deletar perfil");
            console.error(err);
        }
    };

    // Teste de pixel: valida o perfil antes de chamar a API e mostra o resultado num modal.
    const [pixelTest, setPixelTest] = useState(null);

    const handleTestPixel = async (profile) => {
        const { status, platform } = precheckPixelTest(profile);
        if (status !== "ready") {
            setPixelTest({ profile, status, platform });
            return;
        }

        setPixelTest({ profile, status: "sending", platform });
        try {
            const res = await authService.tracking.test_pixel(profile.id);
            setPixelTest({ profile, platform, status: res.data.success ? "success" : "error" });
        } catch (err) {
            const data = err.response?.data || {};
            const msg = data.message || data.error || "";
            if (/c[oó]digo de teste/i.test(msg)) {
                setPixelTest({ profile, platform, status: "missing_code" });
                return;
            }
            // A mensagem genérica do backend não acrescenta nada ao checklist do modal.
            const detail = msg && !/^Falha ao enviar evento de teste$/i.test(msg) ? msg : "";
            setPixelTest({ profile, platform, status: "error", message: detail });
            console.error(err);
        }
    };

    const handleEditFromTest = (profile) => {
        setPixelTest(null);
        handleEdit(profile);
    };

    return (
        <div className="page">
            {/* MODAL DE SELEÇÃO DE PLATAFORMA */}
            <SelectPlatformForTrackingProfile
                open={showPlatformModal}
                onSelectPlatform={handleSelectPlatform}
                onClose={() => setShowPlatformModal(false)}
            />

            <PixelTestModal
                state={pixelTest}
                onClose={() => setPixelTest(null)}
                onEdit={handleEditFromTest}
                onRetry={handleTestPixel}
            />

            {/* MODAL DE CRIAR / EDITAR PERFIL */}
            <TrackingForm
                open={showForm}
                editing={editing}
                platform={selectedPlatform}
                userBots={userBots}
                onBack={() => {
                    setEditing(null);
                    setShowForm(false);
                    setSelectedPlatform(null);
                }}
                onSuccess={handleSave}
            />

            <header className='mb-6 flex space-between flex flex-wrap flex-row'>
                <div>
                    <h1 className='title'>Perfis de Tracking</h1>
                    <p className='text-gray-400'>Crie perfis de rastreamento e associe-os aos seus bots para acompanhar vendas e conversões</p>
                </div>

                <button
                    onClick={handleNewProfile}
                    className='btn-primary text-xs mt-4 md:text-base'
                >
                    <i className="fas fa-plus"></i> Novo Perfil
                </button>
            </header>

            {/* LISTA */}
            <TrackingList
                profiles={trackingProfiles}
                bots={userBots}
                loading={loading}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onCreateNew={handleNewProfile}
                onTestPixel={handleTestPixel}
            />
        </div>
    );
}
